/**
 * Auxilio Finance - Main Application Component
 * Personal Financial Management Platform
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';

// Layout components
import { MainLayout } from './components/layout/MainLayout';

// Pages
import { LandingPage } from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { FinancialGlossary } from './pages/FinancialGlossary';
import { FinancialCalculators } from './pages/FinancialCalculators';
import { FinancialQuestions } from './pages/FinancialQuestions';
import { BudgetTracker } from './pages/BudgetTracker';
import { NetWorthTracker } from './pages/NetWorthTracker';
import { DebtManager } from './pages/DebtManager';
import { StatementAnalyzer } from './pages/StatementAnalyzer';
import { PortfolioAnalyzer } from './pages/PortfolioAnalyzer';
import { CreditAnalyzer } from './pages/CreditAnalyzer';
import LoanPlanning from './pages/LoanPlanning';
import TaxPlanning from './pages/TaxPlanning';
import Goals from './pages/Goals';
import FinancialDocuments from './pages/FinancialDocuments';
import AIFinancialAdvisor from './pages/AIFinancialAdvisor';

// Placeholder pages for features not yet implemented
const PlaceholderPage: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="p-6 max-w-4xl mx-auto">
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{title}</h1>
    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
      <p className="text-amber-800 dark:text-amber-200">{description}</p>
      <p className="text-amber-600 dark:text-amber-400 mt-4 text-sm">Check the roadmap for the planned release schedule.</p>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />

          {/* Protected Routes */}
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/financial-glossary" element={<FinancialGlossary />} />
            <Route path="/calculators" element={<FinancialCalculators />} />
            <Route path="/ask-questions" element={<FinancialQuestions />} />

            {/* Implemented features */}
            <Route path="/budget" element={<BudgetTracker />} />
            <Route path="/net-worth" element={<NetWorthTracker />} />

            {/* Analysis features */}
            <Route path="/statements" element={<StatementAnalyzer />} />
            <Route path="/portfolio" element={<PortfolioAnalyzer />} />
            <Route path="/credit" element={<CreditAnalyzer />} />

            {/* Planning and documents */}
            <Route path="/documents" element={<FinancialDocuments />} />
            <Route path="/loans" element={<LoanPlanning />} />
            <Route path="/taxes" element={<TaxPlanning />} />
            <Route path="/debt" element={<DebtManager />} />
            <Route path="/advisor" element={<AIFinancialAdvisor />} />
            <Route path="/goals" element={<Goals />} />
            <Route
              path="/profile"
              element={<PlaceholderPage title="User Profile" description="Manage your account settings and preferences." />}
            />
            <Route
              path="/billing"
              element={<PlaceholderPage title="Billing" description="View and manage your subscription and billing information." />}
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </I18nextProvider>
  );
};

export default App;
